
import { ChartLoader, ChartWrapper } from './ChartWrapper';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { CONTENT_TYPE_COLORS } from './ui/utils';
import { NoData } from './ui/noData';
import { usePdfMode, PdfDataTable } from './pdf/PdfModeContext';

interface PostTypeInsightsProps {
  platform: string;
  datametrics?: any;
  loading?: boolean;
}

const color = CONTENT_TYPE_COLORS;

export function PostTypeInsights({ loading, platform, datametrics }: PostTypeInsightsProps) {
  const pdfMode = usePdfMode(); // PDF: tooltip values (posts, avg ER) as a table// Post type data with both count and avgER
  const list = datametrics?.insight_by_post_type ?? [];
  const postTypeMap = Object.fromEntries(
    list.map((item: any) => [item.content_type, item.total_count])
  );
const safeFixed = (v:any, digits = 2) =>
  typeof v === "number" && !isNaN(v) ? v.toFixed(digits) : "0.00";
const postTypeData = {
  instagram: [
    {
      type: 'Reels',
      count: (postTypeMap.reels ?? 0) + (postTypeMap.video ?? 0),
      avgER: safeFixed(list?.find((i:any) => i.content_type === 'reels')?.avg_er),
      color: color.Reel
    },
    {
      type: 'Static',
      count: (postTypeMap.static ?? 0) + (postTypeMap.image ?? 0),
      avgER: safeFixed(list?.find((i:any) => i.content_type === 'image')?.avg_er),
      color: color.Photo
    },
    {
      type: 'Carousel',
      count: postTypeMap.carousel ?? 0,
      avgER: safeFixed(list?.find((i:any) => i.content_type === 'carousel')?.avg_er),
      color: color.Carousel
    }
  ].filter(item => item.count > 0),

  facebook: [
    {
      type: 'Image',
      count: list[0]?.image_count ?? 0,
      avgER: safeFixed(list[0]?.avg_image_er),
      color: color.Image
    },
    {
      type: 'Video',
      count: list[0]?.video_count ?? 0,
      avgER: safeFixed(list[0]?.avg_video_er),
      color: color.Video
    },
    {
      type: 'Reel',
      count: list[0]?.reel_count ?? 0,
      avgER: safeFixed(list[0]?.avg_reel_er),
      color: color.Reel
    }
  ].filter(item => item.count > 0),

  youtube: [
    {
      type: 'Video',
      count: list[0]?.video_count ?? 0,
      avgER: safeFixed(list[0]?.avg_video_er),
      color: color.Video
    },
    {
      type: 'Short',
      count: list[0]?.short_count ?? 0,
      avgER: safeFixed(list[0]?.avg_short_er),
      color: color.short
    }
  ].filter(item => item.count > 0)
};


  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white  px-3 py-2 d-flex rounded-lg shadow-lg border border-slate-200">
          <span style={{ display:'flex', alignItems:'center', width:'12px', height:'12px', backgroundColor:data.color, marginRight:'6px', marginTop:'3px'}}></span>
          <span className='flex flex-col'>
          <p className="text-slate-900" style={{fontSize:'13px'}}>{data.type}</p>
          <p className="text-sm text-slate-600" style={{fontSize:'13px'}}>{data.count} Posts</p>
           <p className="text-sm mt-1" style={{color:'#00aae5'}}>Avg ER: {data.avgER}%</p>
          </span>
         
        </div>
      );
    }
    return null;
  };
  const data = postTypeData[platform as keyof typeof postTypeData] || postTypeData.instagram;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Insights by Post</CardTitle>
        <CardDescription>Distribution and performance by content type</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex justify-center">
          {loading && (
            <ChartLoader height={280} />
          )}

        {!loading && data.length > 0 && (
          <ChartWrapper type="pie" height={280}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ type, count }) => `${type}: ${count}`}
                  outerRadius={80}
                  isAnimationActive={!pdfMode}
                  dataKey="count"
                  nameKey="type"
                  style={{ fontSize: '13px' }}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartWrapper>
        )}

        {!loading && data.length === 0 && (
          <NoData message="No post type data available for the selected range" />
        )}
          {/* {data.length > 0 && (
          <ChartWrapper loading={loading} type="pie" height={280}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ type, count }) => `${type}: ${count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="type"
                  style={{ fontSize: '13px' }}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </ChartWrapper>
          )}
          {data.length === 0 && !loading && (
            <NoData message='No post type data available for the selected range' />
          )} */}
        </div>
        {pdfMode && (
          <PdfDataTable
            columns={[{ label: 'Type' }, { label: 'Posts' }, { label: 'Avg ER' }]}
            rows={data.map((d) => [d.type, d.count, `${d.avgER}%`])}
            swatches={data.map((d) => d.color)}
          />
        )}
      </CardContent>
    </Card>
  );
}
